/**
 * Tab identifiers for the global search results page.
 * Used in URL query parameter `type` and Algolia redirect custom data (`tab`).
 */
export const SEARCH_PAGE_TABS = [
  'products',
  'software',
  'documents',
  'resources'
] as const;

export type SearchPageTabType = (typeof SEARCH_PAGE_TABS)[number];

/** Ergonomic aliases for tab type string literals used across the search page. */
export const SearchPageTab = Object.fromEntries(
  SEARCH_PAGE_TABS.map((tab) => [tab.toUpperCase(), tab])
) as {
  readonly PRODUCTS: 'products';
  readonly SOFTWARE: 'software';
  readonly DOCUMENTS: 'documents';
  readonly RESOURCES: 'resources';
};

export const SEARCH_PAGE_TAB_PANEL_IDS = Object.fromEntries(
  SEARCH_PAGE_TABS.map((tab, index) => [tab, `search-tab-panel-${index}`])
) as Record<SearchPageTabType, string>;

export const SEARCH_PAGE_TAB_INDEX = Object.fromEntries(
  SEARCH_PAGE_TABS.map((tab, index) => [tab, index])
) as Record<SearchPageTabType, number>;

export function isSearchPageTabType(
  value: unknown
): value is SearchPageTabType {
  return (
    typeof value === 'string' &&
    (SEARCH_PAGE_TABS as readonly string[]).includes(value)
  );
}

/** Default tab when no or invalid `type` query parameter is present. */
export function getDefaultSearchPageTabType(): SearchPageTabType {
  return SearchPageTab.PRODUCTS;
}

/**
 * Resolves a route `type` query value to a supported search page tab.
 * Falls back to the first tab (`products`) for missing or invalid values.
 */
export function resolveSearchPageTabType(
  queryType: string | undefined | null
): SearchPageTabType {
  if (isSearchPageTabType(queryType)) {
    return queryType;
  }

  return getDefaultSearchPageTabType();
}
